/**
 * HeadlessDashboard - Safety Evaluation Dashboard
 * 
 * Provides controls for batch test runs with visual results grid.
 */

import React, { useState, useEffect, useRef } from "react";
import ResultsGrid from "./ResultsGrid";
import NoiseChart from "./NoiseChart";
import CaseEditor from "../CaseEditor/CaseEditor";
import { AuthProvider, useAuth } from "../../contexts/AuthContext";
import "./HeadlessDashboard.css";

// Inner component that uses auth context
const HeadlessDashboardInner = () => {
    // State
    const [isAuthenticated, setIsAuthenticated] = useState(true); // Default to true (no password)
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");

    const [caseInput, setCaseInput] = useState("1-10");
    const [runName, setRunName] = useState("");
    const [parallelWorkers, setParallelWorkers] = useState(4);
    const [replicates, setReplicates] = useState(1);

    const [availableRuns, setAvailableRuns] = useState([]);
    const [selectedRuns, setSelectedRuns] = useState([]); // Up to 2 for A/B comparison
    const [activeRunIndex, setActiveRunIndex] = useState(0); // Which run is currently displayed
    const [runResults, setRunResults] = useState({}); // Map: filename -> results

    const [currentRunId, setCurrentRunId] = useState(null);
    const [runProgress, setRunProgress] = useState(null);
    const [isRunning, setIsRunning] = useState(false);

    // Transcript modal state
    const [transcriptModal, setTranscriptModal] = useState({ visible: false, caseData: null });

    // Confidence modal state (separate from transcript modal)
    const [confidenceModal, setConfidenceModal] = useState({ visible: false, caseData: null });

    // Harness modal state (models + prompts used for the run)
    const [harnessModal, setHarnessModal] = useState({ visible: false });

    // Toggle between the pass/fail grid and the per-case noise/variance chart
    const [showNoise, setShowNoise] = useState(false);

    // Case editor state
    const [editingCaseId, setEditingCaseId] = useState(null);

    // Auth context
    const { user, isAuthenticated: isGoogleAuthenticated, signIn, isConfigured: isGoogleConfigured, idToken, isLoading } = useAuth();

    const baseURL = window.location.origin === "http://localhost:3000"
        ? "http://localhost:7860"
        : "";

    // Refs for local file inputs
    const fileInputRefA = useRef(null);
    const fileInputRefB = useRef(null);

    // Fetch available runs when password authenticated AND Google authenticated
    useEffect(() => {
        if (isAuthenticated && isGoogleAuthenticated) {
            fetchAvailableRuns();
        }
    }, [isAuthenticated, isGoogleAuthenticated, idToken]);

    // Poll for run progress
    useEffect(() => {
        if (!currentRunId || !isRunning) return;

        const pollInterval = setInterval(async () => {
            try {
                const response = await fetch(`${baseURL}/api/headless/run_status/${currentRunId}`, {
                    headers: { 'Authorization': `Bearer ${idToken}` }
                });
                const data = await response.json();
                if (!response.ok) {
                    // 401/404 etc: don't treat an error body as progress, or the UI spins forever
                    console.error("Run status poll failed:", response.status, data);
                    setRunProgress(prev => ({ ...(prev || {}), pollError: data.error || `HTTP ${response.status}` }));
                    return;
                }
                setRunProgress(data);

                if (data.status === "complete" || data.status === "error") {
                    setIsRunning(false);
                    setCurrentRunId(null);
                    fetchAvailableRuns();
                }
            } catch (error) {
                console.error("Error polling run status:", error);
            }
        }, 2000);

        return () => clearInterval(pollInterval);
    }, [currentRunId, isRunning, baseURL, idToken]);

    // Effect to render Google Sign-In button
    // Effect to render Google Sign-In button
    useEffect(() => {
        // Wait for container to exist
        const container = document.getElementById("google-signin-button");
        if (isGoogleConfigured && !isGoogleAuthenticated && window.google && container) {
            try {
                window.google.accounts.id.renderButton(
                    container,
                    { theme: "outline", size: "large", type: "standard" }
                );
            } catch (err) {
                console.error("Error rendering Google Sign-In button:", err);
            }
        }
    }, [isGoogleConfigured, isGoogleAuthenticated, isLoading]);

    const fetchAvailableRuns = async () => {
        // Only fetch from GCS if user is authenticated with Google
        if (!isGoogleAuthenticated || !idToken) {
            console.log("Not authenticated with Google, skipping GCS fetch");
            setAvailableRuns([]);
            return;
        }

        try {
            const response = await fetch(`${baseURL}/api/test_runs`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (response.status === 403) {
                console.warn("Not authorized to access test runs");
                setAvailableRuns([]);
                return;
            }

            const data = await response.json();
            setAvailableRuns(data.runs || []);
        } catch (error) {
            console.error("Error fetching runs from GCS:", error);
            setAvailableRuns([]);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${baseURL}/api/headless/validate_password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password })
            });

            if (response.ok) {
                setIsAuthenticated(true);
                setPasswordError("");
            } else {
                setPasswordError("Invalid password");
            }
        } catch (error) {
            setPasswordError("Error validating password");
        }
    };

    const handleStartRun = async () => {
        if (!caseInput.trim()) {
            alert("Please enter case numbers (e.g., 1-10, 1,5,9)");
            return;
        }

        const name = runName.trim() || `run_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;

        try {
            const response = await fetch(`${baseURL}/api/headless/start_run`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    cases: caseInput,
                    run_name: name,
                    config: {
                        parallel_workers: Math.max(1, parseInt(parallelWorkers, 10) || 1),
                        replicates: Math.max(1, parseInt(replicates, 10) || 1)
                    }
                })
            });

            const data = await response.json();
            if (data.error) {
                alert(`Error: ${data.error}`);
                return;
            }

            setCurrentRunId(data.run_id);
            setIsRunning(true);
            setRunProgress({ completed: 0, total: data.total_cases, status: "running" });
        } catch (error) {
            console.error("Error starting run:", error);
            alert("Failed to start run");
        }
    };

    const loadRunResults = async (filename) => {
        if (runResults[filename]) return runResults[filename];

        // Require Google auth for GCS access
        if (!isGoogleAuthenticated || !idToken) {
            console.error("Must be authenticated to load run results");
            return null;
        }

        try {
            const response = await fetch(`${baseURL}/api/test_runs/${filename}`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                console.error("Failed to load run:", response.status);
                return null;
            }

            const data = await response.json();
            setRunResults(prev => ({ ...prev, [filename]: data }));
            return data;
        } catch (error) {
            console.error("Error loading run results:", error);
            return null;
        }
    };

    const handleRunSelect = async (filename, index) => {
        if (!filename) return;
        const results = await loadRunResults(filename);
        if (results) {
            setSelectedRuns(prev => {
                const newRuns = [...prev];
                newRuns[index] = filename;
                return newRuns.slice(0, 2);
            });
            setActiveRunIndex(index);
        }
    };

    // Toggle between run A and B
    const toggleActiveRun = () => {
        if (selectedRuns.length === 2 && selectedRuns[1]) {
            setActiveRunIndex(prev => prev === 0 ? 1 : 0);
        }
    };

    // Handle local file selection for Run A or B
    const handleLocalFileSelect = (event, index) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                const localKey = `local:${file.name}`;
                setRunResults(prev => ({ ...prev, [localKey]: data }));
                setSelectedRuns(prev => {
                    const newRuns = [...prev];
                    newRuns[index] = localKey;
                    return newRuns.slice(0, 2);
                });
                setActiveRunIndex(index);
            } catch (error) {
                alert("Invalid JSON file. Please select a valid test run file.");
            }
        };
        reader.readAsText(file);
        event.target.value = ''; // Reset input for re-selection
    };

    // Handle dropdown change - trigger file picker if "Load from file..." selected
    const handleDropdownChange = (value, index) => {
        if (value === '__load_file__') {
            if (index === 0) {
                fileInputRefA.current?.click();
            } else {
                fileInputRefB.current?.click();
            }
        } else {
            handleRunSelect(value, index);
        }
    };

    const currentResults = selectedRuns[activeRunIndex]
        ? runResults[selectedRuns[activeRunIndex]]
        : null;

    // Get display name for current run
    const getCurrentRunDisplay = () => {
        if (!currentResults) return null;
        return `${currentResults.run_name}_${currentResults.run_id}`;
    };

    // Handle case click to show transcript
    const handleCaseClick = (caseData) => {
        setTranscriptModal({ visible: true, caseData });
    };

    // Handle case edit (click on case number in grid)
    const handleCaseEdit = (caseId) => {
        setEditingCaseId(caseId);
    };

    // Handle case save from editor
    const handleCaseSave = (updatedCase) => {
        console.log('Case saved:', updatedCase.case_id);
        setEditingCaseId(null);
        // Optionally refresh results if needed
    };

    // Password screen REMOVED as requested
    // if (!isAuthenticated) { ... }

    return (
        <div className="headless-dashboard">
            <header className="dashboard-header">
                <h1>CHAP-R1 - Safety Evaluation Dashboard</h1>
                <div className="header-auth">
                    {/* Show Sign In only if Google is configured AND not authenticated with Google */}
                    {isGoogleConfigured && !isGoogleAuthenticated && (
                        <div id="google-signin-button" style={{ minHeight: '40px', minWidth: '200px' }}></div>
                    )}
                    {/* Debug info if button missing */}
                    {!isGoogleConfigured && <span style={{ color: 'red' }}>Config Missing</span>}
                    {isGoogleConfigured && !isGoogleAuthenticated && !window.google && <span style={{ color: 'orange' }}>Loading Google...</span>}

                    {isGoogleAuthenticated && user && (
                        <span className="user-badge">
                            {user.picture && <img src={user.picture} alt="" className="user-avatar" />}
                            {user.name || user.email}
                        </span>
                    )}
                </div>
            </header>

            <div className="dashboard-content">
                {/* Single Row Controls */}
                <div className="controls-row">
                    <div className="control-group">
                        <label>Run Name:</label>
                        <input
                            type="text"
                            value={runName}
                            onChange={(e) => setRunName(e.target.value)}
                            placeholder="gemini_test"
                            disabled={isRunning}
                        />
                    </div>

                    <div className="control-group">
                        <label>Cases:</label>
                        <input
                            type="text"
                            value={caseInput}
                            onChange={(e) => setCaseInput(e.target.value)}
                            placeholder="1-10"
                            disabled={isRunning}
                        />
                    </div>

                    <div className="control-group">
                        <label title="Number of cases to run concurrently (separate worker processes)">Workers:</label>
                        <input
                            type="number"
                            min="1"
                            max="16"
                            value={parallelWorkers}
                            onChange={(e) => setParallelWorkers(e.target.value)}
                            disabled={isRunning}
                            style={{ width: '55px' }}
                        />
                    </div>

                    <div className="control-group">
                        <label title="Times to repeat each case, for run-to-run variance analysis">Reps:</label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={replicates}
                            onChange={(e) => setReplicates(e.target.value)}
                            disabled={isRunning}
                            style={{ width: '55px' }}
                        />
                    </div>

                    {/* Progress Button */}
                    <div className="progress-button-wrapper">
                        <button
                            className={`progress-button ${isRunning ? 'running' : ''} ${runProgress?.status === 'complete' ? 'complete' : ''}`}
                            onClick={handleStartRun}
                            disabled={isRunning}
                            style={isRunning && runProgress ? {
                                background: `linear-gradient(90deg, #4CAF50 ${(runProgress.completed / runProgress.total) * 100}%, #2a2a3e ${(runProgress.completed / runProgress.total) * 100}%)`
                            } : {}}
                        >
                            {runProgress?.status === 'complete' ? 'Completed' : isRunning ? 'Running...' : 'Start Run'}
                        </button>
                        {runProgress && (
                            <span className="progress-text">{runProgress.completed} / {runProgress.total} cases{runProgress.pollError ? ` (status check failed: ${runProgress.pollError})` : ""}</span>
                        )}
                    </div>

                    <div className="separator" />

                    <div className="control-group">
                        <label>Run A:</label>
                        <select
                            value={selectedRuns[0]?.startsWith('local:') ? '' : (selectedRuns[0] || "")}
                            onChange={(e) => handleDropdownChange(e.target.value, 0)}
                        >
                            <option value="">{selectedRuns[0]?.startsWith('local:') ? selectedRuns[0].replace('local:', '📁 ') : 'Select...'}</option>
                            <option value="__load_file__">📂 Load from file...</option>
                            {availableRuns.map(run => (
                                <option key={run.filename} value={run.filename}>
                                    {run.run_name} ({run.total_cases}, {(run.overall_pass_rate * 100).toFixed(0)}%)
                                </option>
                            ))}
                        </select>
                        <input
                            type="file"
                            ref={fileInputRefA}
                            accept=".json"
                            style={{ display: 'none' }}
                            onChange={(e) => handleLocalFileSelect(e, 0)}
                        />
                    </div>

                    <div className="control-group">
                        <label>Run B:</label>
                        <select
                            value={selectedRuns[1]?.startsWith('local:') ? '' : (selectedRuns[1] || "")}
                            onChange={(e) => handleDropdownChange(e.target.value, 1)}
                        >
                            <option value="">{selectedRuns[1]?.startsWith('local:') ? selectedRuns[1].replace('local:', '📁 ') : 'Compare...'}</option>
                            <option value="__load_file__">📂 Load from file...</option>
                            {availableRuns.map(run => (
                                <option key={run.filename} value={run.filename}>
                                    {run.run_name} ({run.total_cases}, {(run.overall_pass_rate * 100).toFixed(0)}%)
                                </option>
                            ))}
                        </select>
                        <input
                            type="file"
                            ref={fileInputRefB}
                            accept=".json"
                            style={{ display: 'none' }}
                            onChange={(e) => handleLocalFileSelect(e, 1)}
                        />
                    </div>
                </div>

                {/* Summary Stats with Run Name Toggle */}
                {currentResults?.summary && (
                    <div className="summary-stats">
                        {/* Run Name Toggle Button */}
                        <button
                            className={`run-toggle-button ${selectedRuns.length === 2 && selectedRuns[1] ? 'clickable' : ''}`}
                            onClick={toggleActiveRun}
                            disabled={!(selectedRuns.length === 2 && selectedRuns[1])}
                            title={selectedRuns.length === 2 && selectedRuns[1] ? "Click to toggle between runs" : "Select Run B to compare"}
                        >
                            <span className="run-name">{getCurrentRunDisplay()}</span>
                            {selectedRuns.length === 2 && selectedRuns[1] && (
                                <span className="toggle-indicator">{activeRunIndex === 0 ? "A" : "B"}</span>
                            )}
                        </button>

                        <div className="stat-card">
                            <span className="stat-value">{(currentResults.summary.overall_pass_rate * 100).toFixed(0)}%</span>
                            <span className="stat-label">Pass</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{(currentResults.summary.critical_failure_rate * 100).toFixed(0)}%</span>
                            <span className="stat-label">CF</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{(currentResults.summary.quality_failure_rate * 100).toFixed(0)}%</span>
                            <span className="stat-label">QF</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{(currentResults.summary.excellence_rate * 100).toFixed(0)}%</span>
                            <span className="stat-label">EX</span>
                        </div>
                        {currentResults.summary.avg_confidence_score > 0 && (
                            <div
                                className="stat-card"
                                title={currentResults.summary.confidence_colors ?
                                    `GREEN: ${currentResults.summary.confidence_colors.GREEN} | YELLOW: ${currentResults.summary.confidence_colors.YELLOW} | RED: ${currentResults.summary.confidence_colors.RED} | ERROR: ${currentResults.summary.confidence_colors.ERROR}` : ''}
                            >
                                <span className="stat-value">{currentResults.summary.avg_confidence_score}</span>
                                <span className="stat-label">Conf</span>
                            </div>
                        )}
                        {currentResults.summary.token_usage_summary?.estimated_cost_usd > 0 && (
                            <div
                                className="stat-card"
                                title={`${(currentResults.summary.token_usage_summary.total_tokens / 1000000).toFixed(2)}M tokens across ${currentResults.summary.token_usage_summary.calls} calls (estimated)`}
                            >
                                <span className="stat-value">${currentResults.summary.token_usage_summary.estimated_cost_usd.toFixed(2)}</span>
                                <span className="stat-label">Cost</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Grid / Noise view toggle */}
                {currentResults && (currentResults.summary?.replicates > 1 ||
                    currentResults.config?.replicates > 1) && (
                    <div className="view-toggle">
                        <button
                            className={`view-toggle-btn ${!showNoise ? 'active' : ''}`}
                            onClick={() => setShowNoise(false)}
                        >Grid</button>
                        <button
                            className={`view-toggle-btn ${showNoise ? 'active' : ''}`}
                            onClick={() => setShowNoise(true)}
                        >Noise</button>
                    </div>
                )}

                {/* Results Grid or Noise Chart */}
                {currentResults && (
                    showNoise
                        ? <NoiseChart results={currentResults} />
                        : <ResultsGrid
                            results={currentResults}
                            onCaseClick={handleCaseClick}
                            onCaseEdit={handleCaseEdit}
                            onCriterionClick={handleCaseClick}
                        />
                )}
            </div>

            {/* Transcript Modal */}
            {transcriptModal.visible && transcriptModal.caseData && (
                <div className="modal-overlay" onClick={() => setTranscriptModal({ visible: false, caseData: null })}>
                    <div className="transcript-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{transcriptModal.caseData.case_name}</h2>
                            <button className="close-button" onClick={() => setTranscriptModal({ visible: false, caseData: null })}>×</button>
                        </div>
                        <div className="modal-content">
                            {/* Metadata Section */}
                            <div className="metadata-section">
                                <div className="metadata-item">
                                    <span className="metadata-label">Start Time:</span>
                                    <span className="metadata-value">{transcriptModal.caseData.start_time || "N/A"}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Elapsed Time:</span>
                                    <span className="metadata-value">
                                        {transcriptModal.caseData.elapsed_seconds
                                            ? `${Math.floor(transcriptModal.caseData.elapsed_seconds / 60)}:${String(Math.floor(transcriptModal.caseData.elapsed_seconds % 60)).padStart(2, '0')}`
                                            : "N/A"}
                                    </span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Questions Asked:</span>
                                    <span className="metadata-value">{transcriptModal.caseData.question_count || "N/A"}</span>
                                </div>
                                {transcriptModal.caseData.confidence_score && (
                                    <div className="metadata-item">
                                        <span className="metadata-label">Confidence:</span>
                                        <span
                                            className={`confidence-badge ${transcriptModal.caseData.confidence_score.Safety_Flag_Color === 'GREEN' ? 'green' :
                                                transcriptModal.caseData.confidence_score.Safety_Flag_Color === 'YELLOW' ? 'yellow' :
                                                    transcriptModal.caseData.confidence_score.Safety_Flag_Color === 'RED' ? 'red' : 'error'
                                                }`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConfidenceModal({ visible: true, caseData: transcriptModal.caseData });
                                            }}
                                            title="Click for full confidence breakdown"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {transcriptModal.caseData.confidence_score.Quality_Score}/100
                                        </span>
                                    </div>
                                )}
                                <div className="metadata-item">
                                    <span
                                        className="harness-badge"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setHarnessModal({ visible: true });
                                        }}
                                        title="Models and prompts used for this test run"
                                    >
                                        HARNESS
                                    </span>
                                </div>
                            </div>

                            {/* Clinical Synthesis - FIRST */}
                            {transcriptModal.caseData.clinical_synthesis && (
                                <div className="synthesis-section">
                                    <h3>Clinical Synthesis</h3>
                                    <pre>{transcriptModal.caseData.clinical_synthesis}</pre>
                                </div>
                            )}

                            {/* Generated Report - SECOND */}
                            {transcriptModal.caseData.report && (
                                <div className="report-section">
                                    <h3>Generated Report</h3>
                                    <pre>{transcriptModal.caseData.report}</pre>
                                </div>
                            )}

                            {/* Evaluation Report - THIRD */}
                            <div className="evaluation-section">
                                <h3>Evaluation Report</h3>
                                {transcriptModal.caseData.evaluation_report ? (
                                    <div className="evaluation-rendered" dangerouslySetInnerHTML={{ __html: transcriptModal.caseData.evaluation_report }} />
                                ) : (
                                    <pre>Not generated. Run a new test with the latest code to generate evaluation reports automatically.</pre>
                                )}
                            </div>

                            {/* Interview Transcript with Thinking - LAST (longest section) */}
                            <div className="transcript-section">
                                <h3>Interview Transcript (with AI Thinking)</h3>
                                <pre>{transcriptModal.caseData.transcript || "No transcript available"}</pre>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Case Editor Modal */}
            {editingCaseId && (
                <CaseEditor
                    caseId={editingCaseId}
                    onClose={() => setEditingCaseId(null)}
                    onSave={handleCaseSave}
                />
            )}

            {/* Interview Confidence Modal (separate) */}
            {confidenceModal.visible && confidenceModal.caseData && (
                <div className="modal-overlay" onClick={() => setConfidenceModal({ visible: false, caseData: null })}>
                    <div className="transcript-modal confidence-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Interview Confidence — {confidenceModal.caseData.case_name}</h2>
                            <button className="close-button" onClick={() => setConfidenceModal({ visible: false, caseData: null })}>×</button>
                        </div>
                        <div className="modal-content">
                            {confidenceModal.caseData.confidence_score ? (
                                <>
                                    <div className="confidence-modal-header">
                                        <span className={`confidence-dot-large ${confidenceModal.caseData.confidence_score.Safety_Flag_Color === 'GREEN' ? 'green' :
                                            confidenceModal.caseData.confidence_score.Safety_Flag_Color === 'YELLOW' ? 'yellow' :
                                                confidenceModal.caseData.confidence_score.Safety_Flag_Color === 'RED' ? 'red' : 'error'
                                            }`}></span>
                                        <span className="confidence-modal-score">
                                            {confidenceModal.caseData.confidence_score.Quality_Score}/100
                                        </span>
                                        <span className={`confidence-modal-flag ${confidenceModal.caseData.confidence_score.Safety_Flag_Color === 'GREEN' ? 'green' :
                                            confidenceModal.caseData.confidence_score.Safety_Flag_Color === 'YELLOW' ? 'yellow' :
                                                confidenceModal.caseData.confidence_score.Safety_Flag_Color === 'RED' ? 'red' : 'error'
                                            }`}>
                                            {confidenceModal.caseData.confidence_score.Safety_Flag_Color}
                                        </span>
                                    </div>
                                    <div className="confidence-modal-reason">
                                        {confidenceModal.caseData.confidence_score.Reason}
                                    </div>
                                    {confidenceModal.caseData.confidence_score.dimensions &&
                                        Object.keys(confidenceModal.caseData.confidence_score.dimensions).length > 0 && (
                                            <table className="confidence-modal-table">
                                                <thead>
                                                    <tr>
                                                        <th>Dimension</th>
                                                        <th>Weight</th>
                                                        <th>Score</th>
                                                        <th>Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Object.entries(confidenceModal.caseData.confidence_score.dimensions).map(([name, data]) => {
                                                        const weights = { safety_and_risk: '40%', information_density: '30%', hallucination_check: '20%', patient_engagement: '10%' };
                                                        return (
                                                            <tr key={name}>
                                                                <td style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                                                    {name.replace(/_/g, ' ')}
                                                                </td>
                                                                <td style={{ textAlign: 'center', color: '#666' }}>
                                                                    {weights[name] || '—'}
                                                                </td>
                                                                <td style={{
                                                                    textAlign: 'center',
                                                                    fontWeight: 700,
                                                                    color: data.score >= 90 ? '#2E7D32' : data.score >= 70 ? '#E65100' : '#C62828'
                                                                }}>
                                                                    {data.score}
                                                                </td>
                                                                <td style={{ fontSize: '13px', color: '#444' }}>
                                                                    {data.details}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}
                                </>
                            ) : (
                                <p>No confidence score available for this case.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Harness Modal — models + prompts used for the run */}
            {harnessModal.visible && currentResults && (
                <div className="modal-overlay" onClick={() => setHarnessModal({ visible: false })}>
                    <div className="transcript-modal harness-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Test Harness — {currentResults.run_name}</h2>
                            <button className="close-button" onClick={() => setHarnessModal({ visible: false })}>×</button>
                        </div>
                        <div className="modal-content">
                            <table className="harness-table">
                                <thead>
                                    <tr>
                                        <th>Agent</th>
                                        <th>Model</th>
                                        <th>Thinking</th>
                                        <th>Temp</th>
                                        <th>Prompt file</th>
                                        <th>SHA-256</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(currentResults.model_config || {}).map(([role, cfg]) => (
                                        <tr key={role}>
                                            <td style={{ textTransform: 'capitalize', fontWeight: 600 }}>{role.replace(/_/g, ' ')}</td>
                                            <td>{cfg.model}</td>
                                            <td>{cfg.thinking}</td>
                                            <td>{typeof cfg.temperature === 'number' ? cfg.temperature.toFixed(1) : '—'}</td>
                                            <td>{cfg.prompt_file}</td>
                                            <td className="harness-sha" title={cfg.prompt_sha256 || ''}>
                                                {cfg.prompt_sha256 ? cfg.prompt_sha256.slice(0, 12) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <h3 className="harness-prompts-heading">Prompts</h3>
                            {Object.entries(currentResults.model_config || {}).map(([role, cfg]) => {
                                const text = (currentResults.prompt_snapshots || {})[cfg.prompt_file];
                                return (
                                    <div className="harness-prompt-block" key={role}>
                                        <h4>{role.replace(/_/g, ' ')} — {cfg.prompt_file}</h4>
                                        <pre>{text || '(prompt text not captured in this run — re-run with the latest code to snapshot all prompts)'}</pre>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Wrapper component that provides auth context
const HeadlessDashboard = () => {
    return (
        <AuthProvider>
            <HeadlessDashboardInner />
        </AuthProvider>
    );
};

export default HeadlessDashboard;
